import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
import { faAngleDown, faRectangleList, faSignOut, faTable, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseQueryResult } from '@tanstack/react-query';
import { Button, Dropdown, MenuProps } from 'antd';
import { useCallContext } from 'helpers/hooks/use-context';
import { useLogout } from 'helpers/hooks/use-logout';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import { KlwMenu } from '../types/menu';
import { Conditional } from 'components/conditional/conditional';

interface ProfileAdminMenuItemProps {
  menu: UseQueryResult<KlwMenu>;
}
export const ProfileAdminMenuItem: FC<ProfileAdminMenuItemProps> = ({ menu }) => {
  const context = useCallContext();
  const nav = useNavigate();
  const logout = useLogout();

  const isNieuweGebruiker = () => {
    if (!context.data) return false;
    if (context?.data?.gebruikerId === 0) return true;
    return false;
  };

  const isNietActiveRelatie = () => {
    if (!context.data) return false;
    return [2, 3, 4].includes(context?.data?.relatieStatusId ?? 0);
  };

  const machtigingenBeheren: MenuProps['items'] = [
    {
      label: 'Beheren',
      key: 'beheren',
      type: 'group',
      children: [
        { type: 'divider' },
        {
          label: 'Machtigingen overzicht',
          key: 'machtigingen',
          onClick: () => nav('/mandates'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
        {
          label: 'Goedgekeurde machtigingen',
          key: 'approved',
          onClick: () => nav('/approved-participations'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
        {
          label: 'Openstaande machtigingen',
          key: 'unapproved',
          onClick: () => nav('/unapproved-participations'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
        {
          label: 'Ingetrokken machtigingen',
          key: 'revoked',
          onClick: () => nav('/revoked-participations'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
      ],
    },
  ];
  const adviesorganisatiesBeheren: MenuProps['items'] = [
    {
      label: 'Beheren voor adviesorganisaties',
      key: 'Beheren voor adviesorganisaties',
      type: 'group',
      children: [
        { type: 'divider' },
        {
          label: 'Overzicht machtigingen',
          key: 'manage-participations',
          onClick: () => nav('/manage-participations'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
        {
          label: 'Projecten',
          key: 'manage-purposes',
          onClick: () => nav('/manage-purposes'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
      ],
    },
  ];
  const getMachtigingMenu = () => {
    const menu: MenuProps['items'] = [];
    if (context?.data?.gebruikerRollen.includes('Deelnemer') || context?.data?.volmacht) menu.push(...machtigingenBeheren);
    if (context?.data?.gebruikerRollen.includes('Afnemer')) {
      if (menu.length > 0) menu.push({ type: 'divider' });
      menu.push(...adviesorganisatiesBeheren);
    }
    return menu;
  };
  const kengetallenTool: MenuProps['items'] = [
    {
      label: 'Kengetallen Tool',
      key: 'Kengetallen',
      type: 'group',
      children: [
        {
          label: 'Kengetallen Tool',
          key: 'kengetallentool',
          onClick: () => nav('/kengetallentool'),
          icon: <FontAwesomeIcon icon={faRectangleList} />,
        },
      ],
    },
    { type: 'divider' },
  ];
  const rapportage: MenuProps['items'] = [
    {
      label: 'Rapportage',
      key: 'Rapportage',
      type: 'group',
      children: [
        {
          label: 'Historische rapportages opvragen',
          key: 'rapportages',
          onClick: () => nav('/rapportage/historisch/index'),
          icon: <FontAwesomeIcon icon={faFileAlt} />,
        },
      ],
    },
    { type: 'divider' },
  ];
  const serviceEnBeheer: MenuProps['items'] = [
    {
      label: 'Service & Beheer',
      key: 'S&B',
      type: 'group',
      children: [
        {
          label: 'Beheer dashboard',
          key: 'sbDashboard',
          onClick: () => nav('/sbDashboard'),
          icon: <FontAwesomeIcon icon={faTable} />,
        },
      ],
    },
    { type: 'divider' },
  ];
  const beheren: MenuProps['items'] = [
    {
      label: 'Beheren',
      key: 'beheren',
      type: 'group',
      children: [
        {
          label: 'Gebruikersprofiel',
          key: 'Gebruikersprofiel',
          onClick: () => nav('/profiel/index'),
          icon: <FontAwesomeIcon icon={faUser} />,
        },
        {
          label: 'Afmelden',
          key: 'Afmelden',
          onClick: () => {
            void logout();
          },
          icon: <FontAwesomeIcon icon={faSignOut} />,
        },
      ],
    },
  ];
  const getProfielMenu = () => {
    //bij nieuwe gebruikers alleen profiel en uitloggen als opties
    if (isNieuweGebruiker() || isNietActiveRelatie()) return beheren;
    const profielMenu: MenuProps['items'] = [
      {
        label: (
          <>
            <p style={{ color: '#666', fontSize: 12, lineHeight: '4px', marginTop: 5, marginBottom: '16px' }}>Ingelogd als:</p>
            <p style={{ color: '#999', fontSize: 13, lineHeight: '4px', marginTop: 5 }}>{menu?.data?.gebruikersnaam}</p>
            <p style={{ color: '#999', fontSize: 13, lineHeight: '4px', marginTop: 5 }}>
              {context?.data?.gebruikerRollen.includes('Afnemer') ? menu?.data?.bedrijfsnaam : menu?.data?.deelnemerBedrijfsnaam}
            </p>
          </>
        ),
        style: { marginBottom: '-10px' },
        key: '0',
        type: 'group',
      },
      { type: 'divider' },
    ];
    if (context?.data?.gebruikerRollen.includes('Afnemer')) profielMenu.push(...kengetallenTool);
    profielMenu.push(...rapportage);
    if (context?.data?.gebruikerIsSBMedewerker) profielMenu.push(...serviceEnBeheer);
    profielMenu.push(...beheren);

    return profielMenu;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', height: 84 }}>
      <Conditional condition={context.data?.gebruikerId !== 0 && !isNieuweGebruiker() && !isNietActiveRelatie()}>
        <Dropdown
          trigger={['click']}
          menu={{
            items: getMachtigingMenu(),
          }}>
          <Button icon={<FontAwesomeIcon icon={faAngleDown} />} style={{ textAlign: 'right', height: 30 }} type="link">
            Machtigingen
          </Button>
        </Dropdown>
      </Conditional>
      <Dropdown
        trigger={['click']}
        menu={{
          items: getProfielMenu(),
        }}>
        <Button icon={<FontAwesomeIcon icon={faAngleDown} />} style={{ textAlign: 'right', height: 30 }} type="link">
          Profiel
        </Button>
      </Dropdown>
    </div>
  );
};
